import {
  BRAND,
  DEFAULT_SHARE_THEME,
  isShareTheme,
  type AdminSession,
  type FileRecord,
  type PublicShareMetadata,
  type ShareTheme,
  type UnlockShareResponse,
} from "@xiaotian-vault/shared";

export const DEMO_SHARE_TOKEN = "demo-local";
export const DEMO_EXPIRED_TOKEN = "demo-expired";
export const DEMO_PASSKEY = "1234";
const DEMO_THEME_KEY = "vault-demo-public-theme";

export function isLocalDemoEnabled(): boolean {
  return import.meta.env.DEV;
}

export function isDemoAdminRoute(): boolean {
  return isLocalDemoEnabled() && window.location.pathname === "/admin/demo";
}

export function isDemoShareToken(token: string): boolean {
  return isLocalDemoEnabled() && token === DEMO_SHARE_TOKEN;
}

export function isDemoExpiredToken(token: string): boolean {
  return isLocalDemoEnabled() && token === DEMO_EXPIRED_TOKEN;
}

export function demoAdminSession(): AdminSession {
  return {
    username: "demo-admin",
    csrfToken: "local-demo",
  };
}

export function demoPublicShareTheme(): ShareTheme {
  const saved = window.localStorage.getItem(DEMO_THEME_KEY);
  return isShareTheme(saved) ? saved : DEFAULT_SHARE_THEME;
}

export function saveDemoPublicShareTheme(theme: ShareTheme): void {
  window.localStorage.setItem(DEMO_THEME_KEY, theme);
}

export function demoPublicMetadata(): PublicShareMetadata {
  return {
    token: DEMO_SHARE_TOKEN,
    fileName: "weekend-photos-and-video.zip",
    sizeBytes: 5 * 1024 * 1024 * 1024,
    mimeType: "application/zip",
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    brand: BRAND,
    theme: demoPublicShareTheme(),
  };
}

export function demoUnlockResponse(): UnlockShareResponse {
  const contents = [
    "Elliot's file share local demo",
    "",
    "This is a tiny text download that stands in for the sample 5 GB archive shown in the UI.",
    `Generated locally at ${new Date().toISOString()}`,
  ].join("\n");

  return {
    fileName: "sample-download.txt",
    sizeBytes: 5 * 1024 * 1024 * 1024,
    mimeType: "text/plain",
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    downloadUrl: `data:text/plain;charset=utf-8,${encodeURIComponent(contents)}`,
    previewUrl: null,
    previewKind: null,
  };
}

export function demoFiles(): FileRecord[] {
  const now = new Date();
  const createdAt = new Date(now.getTime() - 42 * 60 * 1000).toISOString();
  const lastDownloadedAt = new Date(
    now.getTime() - 8 * 60 * 1000,
  ).toISOString();

  return [
    {
      id: "demo-file-1",
      uploadId: "demo-upload-1",
      originalName: "weekend-photos-and-video.zip",
      sizeBytes: 5 * 1024 * 1024 * 1024,
      mimeType: "application/zip",
      status: "ready",
      createdAt,
      completedAt: createdAt,
      deletedAt: null,
      shares: [
        {
          id: "demo-share-1",
          token: DEMO_SHARE_TOKEN,
          fileId: "demo-file-1",
          passkey: DEMO_PASSKEY,
          enabled: true,
          expiresAt: new Date(
            now.getTime() + 24 * 60 * 60 * 1000,
          ).toISOString(),
          createdAt,
          updatedAt: createdAt,
          downloadCount: 3,
          lastDownloadedAt,
        },
      ],
    },
    {
      id: "demo-file-2",
      uploadId: "demo-upload-2",
      originalName: "photo-preview.jpg",
      sizeBytes: 3.8 * 1024 * 1024,
      mimeType: "image/jpeg",
      status: "ready",
      createdAt: new Date(now.getTime() - 5 * 60 * 60 * 1000).toISOString(),
      completedAt: new Date(now.getTime() - 5 * 60 * 60 * 1000).toISOString(),
      deletedAt: null,
      shares: [],
    },
  ];
}

export function demoUploadedFileRecord(input: {
  name: string;
  size: number;
  type: string;
}): FileRecord {
  const now = new Date().toISOString();
  const id = `demo-file-${crypto.randomUUID()}`;

  return {
    id,
    uploadId: `demo-upload-${id}`,
    originalName: input.name,
    sizeBytes: input.size,
    mimeType: input.type || "application/octet-stream",
    status: "ready",
    createdAt: now,
    completedAt: now,
    deletedAt: null,
    shares: [],
  };
}
